/**
 * Regex tables and matchers that detect AI-authored timestamp mentions in
 * prose. Pure string-in, structured-out — no React, no markdown AST. The
 * remark plugin (see ../remarkTimestampMentions.ts) and the label
 * heuristics (see ./labelHeuristics.ts) both consume these matchers.
 *
 * Four wall-clock units are recognized:
 *
 *   t=<digits>ns                → nanoseconds
 *   t=<digits>(µs|μs|us)        → microseconds
 *   t=<digits>ms                → milliseconds
 *   t=<digits>[.<frac>]s        → seconds (lossless via string math)
 *
 * The `t=` + unit envelope is the sole discriminator for prose mentions.
 * Bare digits and any other prefix pass through unchanged.
 */

export interface TimestampMention {
  start: number;
  end: number;
  raw: string;
  ns: bigint;
}

// Digit widths are unconstrained so that clock-broken recordings anchored
// near 1970 still round-trip. Bare decimal numbers in prose ("took 3.4s")
// remain rejected because they lack the `t=` prefix.
const ENVELOPED_REGEXES: {
  re: RegExp;
  toNs: (intPart: string, fracPart?: string) => bigint;
}[] = [
  {
    // ns
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)\s*ns\b/g,
    toNs: (intPart) => BigInt(intPart),
  },
  {
    // µs — any of the three micro spellings
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)\s*(?:µs|μs|us)\b/g,
    toNs: (intPart) => BigInt(intPart) * 1_000n,
  },
  {
    // ms
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)\s*ms\b/g,
    toNs: (intPart) => BigInt(intPart) * 1_000_000n,
  },
  {
    // s — optional fractional (up to 9 digits) kept lossless
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)(?:\.(\d{1,9}))?\s*s\b/g,
    toNs: (intPart, fracPart) => {
      const frac = (fracPart ?? "").padEnd(9, "0").slice(0, 9);
      return BigInt(intPart) * 1_000_000_000n + BigInt(frac);
    },
  },
];

export function findTimestampMentions(text: string): TimestampMention[] {
  const mentions: TimestampMention[] = [];
  for (const { re, toNs } of ENVELOPED_REGEXES) {
    re.lastIndex = 0;
    for (const m of text.matchAll(re)) {
      const start = m.index ?? 0;
      mentions.push({
        start,
        end: start + m[0].length,
        raw: m[0],
        ns: toNs(m[1], m[2]),
      });
    }
  }
  // Overlap resolution depends on TWO coupled invariants — preserve both
  // when editing this code:
  //   (a) `ENVELOPED_REGEXES` is iterated in declaration order, so the
  //       higher-precision unit (ns first) is pushed before less-specific
  //       overlapping units. Reordering the array would flip precedence.
  //   (b) `Array.prototype.sort` is stable in V8 / modern engines, so on
  //       equal `(start, -end)` keys the first-pushed match wins. Swapping
  //       in `Array.prototype.toSorted` or any unstable sort would silently
  //       break tie-breaking and let lower-precision matches win.
  mentions.sort((a, b) => a.start - b.start || b.end - a.end);
  const out: TimestampMention[] = [];
  let lastEnd = -1;
  for (const m of mentions) {
    if (m.start >= lastEnd) {
      out.push(m);
      lastEnd = m.end;
    }
  }
  return out;
}

/**
 * True when the entire trimmed string is a single `t=<n><unit>` mention —
 * used by Timestamp to decide whether a link label was AI-generated (render
 * as the user's format) or a custom user label (preserve verbatim).
 */
export function isTimestampMention(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return false;
  }
  const mentions = findTimestampMentions(trimmed);
  return (
    mentions.length === 1 &&
    mentions[0].start === 0 &&
    mentions[0].end === trimmed.length
  );
}

// ---------------------------------------------------------------------------
// Bare-unit mentions (no `t=` prefix)
// ---------------------------------------------------------------------------
// Used to rescue timestamps the AI wrote with the `t=` prefix outside a
// backticked highlight (e.g. ``t=`1771481251863355727ns```). The digits+unit
// half ends up as an inlineCode node; the `t=` preamble sits in the
// preceding text sibling.

const BARE_UNIT_REGEXES: {
  re: RegExp;
  toNs: (intPart: string, fracPart?: string) => bigint;
}[] = [
  { re: /^\s*(\d+)\s*ns\s*$/, toNs: (i) => BigInt(i) },
  { re: /^\s*(\d+)\s*(?:µs|μs|us)\s*$/, toNs: (i) => BigInt(i) * 1_000n },
  { re: /^\s*(\d+)\s*ms\s*$/, toNs: (i) => BigInt(i) * 1_000_000n },
  {
    re: /^\s*(\d+)(?:\.(\d{1,9}))?\s*s\s*$/,
    toNs: (i, f) => {
      const frac = (f ?? "").padEnd(9, "0").slice(0, 9);
      return BigInt(i) * 1_000_000_000n + BigInt(frac);
    },
  },
];

/** Match a unit-only mention (no `t=` prefix) that fills the entire string. */
export function matchBareUnitMention(
  text: string,
): { ns: bigint; raw: string } | null {
  for (const { re, toNs } of BARE_UNIT_REGEXES) {
    const m = text.match(re);
    if (m) {
      return { ns: toNs(m[1], m[2]), raw: text };
    }
  }
  return null;
}

/**
 * Match a trailing `t\s*=\s*` on a text node, returning the prefix to keep
 * and confirming the suffix existed. The prefix's trailing whitespace is
 * preserved so rewritten output keeps the natural spacing around the
 * mention (`"found "` stays `"found "`). Approximation marks (`~`, `≈`)
 * are consumed with the `t=` token.
 */
export function stripTrailingTEquals(text: string): string | null {
  const m = text.match(/^(.*?)[~≈]?t\s*=\s*$/s);
  return m ? m[1] : null;
}
